import {
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { TooltipDelayed } from '../tooltipDelayed/TooltipDelayed';
import {
  VerticalTab,
  MenuPosition,
  VerticalTabPanelProps,
} from './VerticalTabPanel';

export enum TabButtonPosition {
  TOP = 'top',
  CENTER = 'center',
  BOTTOM = 'bottom',
}

export enum ButtonStyle {
  CONNECT = 'connect',
  MODUS = 'modus',
}

// Animation constants
const DEFAULT_CONTENT_WIDTH_PX = 400;
// Connect style: 40px matches existing design system
const DEFAULT_MENU_WIDTH_PX = 40;
// Modus style: 48px provides 8px padding (4px each side) around 32px buttons
const MODUS_MENU_WIDTH_PX = 48;
const ANIMATION_DURATION_MS = 300;
// Default spacing for Modus style top/bottom margins (equivalent to Tailwind's mt-6/mb-6)
const DEFAULT_MODUS_SPACING_PX = 24;
const LAYOUT_READY_MAX_FRAMES = 4;

const hasMeasurableLayout = (element: HTMLElement): boolean =>
  element.clientHeight > 0 && element.clientWidth > 0;

interface AnimatedTabPanelContentProps<T extends string> {
  tab: VerticalTabAnimated<T>;
  tabButtonStyle?: ButtonStyle;
  menuPosition?: MenuPosition;
}

/**
 * Defers mounting tab content until the panel container has a non-zero size.
 * Animated width transitions (and `%`-height flex parents) can leave virtualized
 * descendants measuring 0×0 on first paint; mounting early pins a too-small
 * viewport (e.g. SelectionTree's 240px fallback).
 */
const AnimatedTabPanelContent = <T extends string>(
  props: AnimatedTabPanelContentProps<T>
): JSX.Element => {
  let panelRef: HTMLDivElement | undefined;
  const [canMountContent, setCanMountContent] = createSignal(false);
  let layoutFrameAttempts = 0;

  const tryEnableContentMount = (): void => {
    const panel = panelRef;
    if (!panel || canMountContent()) {
      return;
    }

    if (hasMeasurableLayout(panel)) {
      setCanMountContent(true);
      return;
    }

    layoutFrameAttempts += 1;
    if (layoutFrameAttempts >= LAYOUT_READY_MAX_FRAMES) {
      setCanMountContent(true);
    }
  };

  onMount(() => {
    tryEnableContentMount();

    const scheduleLayoutCheck = (): void => {
      requestAnimationFrame(() => {
        tryEnableContentMount();
        if (!canMountContent()) {
          scheduleLayoutCheck();
        }
      });
    };
    scheduleLayoutCheck();

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => tryEnableContentMount());
    if (panelRef) {
      resizeObserver?.observe(panelRef);
    }

    const mountFallbackTimer = window.setTimeout(
      () => setCanMountContent(true),
      ANIMATION_DURATION_MS
    );

    onCleanup(() => {
      resizeObserver?.disconnect();
      window.clearTimeout(mountFallbackTimer);
    });
  });

  const panelClassName = (): string => {
    const modusLeftPanel =
      props.tabButtonStyle === ButtonStyle.MODUS &&
      props.menuPosition === MenuPosition.LEFT;
    return `flex flex-col h-full min-h-0 overflow-hidden relative ${
      modusLeftPanel ? 'shadow-lg border-l border-base-200' : ''
    } bg-base-page text-base-content`;
  };

  return (
    <div
      ref={(element) => {
        panelRef = element;
      }}
      role="tabpanel"
      id={`vertical-tab-content-${props.tab.name}`}
      aria-labelledby={`vertical-tab-menu-${props.tab.name}`}
      data-testid={`vertical-tab-content-${props.tab.name}`}
      class={panelClassName()}
    >
      <Show when={canMountContent()}>
        <Dynamic component={props.tab.content!} />
      </Show>
    </div>
  );
};

export interface VerticalTabAnimated<T extends string> extends VerticalTab<T> {
  /**
   * Preferred position for the tab button in the vertical menu.
   * - TOP: Position at the top of the menu
   * - CENTER: Position in the center of the menu (default)
   * - BOTTOM: Position at the bottom of the menu
   *
   * @default ButtonPosition.CENTER
   */
  tabButtonPosition?: TabButtonPosition;
  /**
   * @deprecated Use `buttonPosition` instead. This property is maintained for backward compatibility.
   */
  positionBottom?: boolean;
}

export interface VerticalTabPanelAnimatedProps<T extends string>
  extends VerticalTabPanelProps<T> {
  tabs: VerticalTabAnimated<T>[];
  /**
   * Width of the content panel in pixels.
   * Default is 400.
   */
  contentWidth?: number;
  /**
   * Style of the tab buttons.
   * - CONNECT: Connect style button (default) - 40px menu, compact styling
   * - MODUS: Modus toolbar style - 48px menu, 32x32 buttons with rounded corners
   *
   * @default ButtonStyle.CONNECT
   * @example
   * <VerticalTabPanelAnimated
   *   tabButtonStyle={ButtonStyle.MODUS}
   *   tabs={tabs}
   *   menuPosition={MenuPosition.LEFT}
   * />
   */
  tabButtonStyle?: ButtonStyle;
  /**
   * Custom top spacing in pixels for the top-positioned tabs container.
   * If not provided, defaults to 24px (mt-6) for Modus style and 0px for Connect style.
   *
   * @default undefined (uses style-specific defaults)
   * @example
   * <VerticalTabPanelAnimated
   *   topSpacing={32}
   *   tabs={tabs}
   *   menuPosition={MenuPosition.LEFT}
   * />
   */
  topSpacing?: number;
  /**
   * Custom bottom spacing in pixels for the bottom-positioned tabs container.
   * If not provided, defaults to 24px (mb-6) for Modus style and 0px for Connect style.
   *
   * @default undefined (uses style-specific defaults)
   * @example
   * <VerticalTabPanelAnimated
   *   bottomSpacing={32}
   *   tabs={tabs}
   *   menuPosition={MenuPosition.LEFT}
   * />
   */
  bottomSpacing?: number;
}

export const VerticalTabPanelAnimated = <T extends string>(
  props: VerticalTabPanelAnimatedProps<T>
): JSX.Element => {
  const [selectedTab, setSelectedTab] = createSignal<T | undefined>(undefined);
  const [tabs, setTabs] = createSignal<VerticalTabAnimated<T>[]>([]);
  // Keep content mounted during close animation
  const [displayTab, setDisplayTab] = createSignal<T | undefined>(undefined);

  // Animation configuration
  const contentWidth = (): number =>
    props.contentWidth ?? DEFAULT_CONTENT_WIDTH_PX;
  const menuWidth = createMemo((): number =>
    props.tabButtonStyle === ButtonStyle.MODUS
      ? MODUS_MENU_WIDTH_PX
      : DEFAULT_MENU_WIDTH_PX
  );
  const isPanelOpen = (): boolean => !!selectedTab() && !!showTab()?.content;

  /**
   * Helper to determine the effective button position, prioritizing buttonPosition over deprecated positionBottom
   */
  const getEffectivePosition = (
    tab: VerticalTabAnimated<T>
  ): TabButtonPosition => {
    // If buttonPosition is explicitly set, use it
    if (tab.tabButtonPosition !== undefined) {
      return tab.tabButtonPosition;
    }
    // Fall back to deprecated positionBottom for backward compatibility
    // eslint-disable-next-line @typescript-eslint/no-deprecated, sonarjs/deprecation -- Intentional use of deprecated property for backward compatibility
    if (tab.positionBottom === true) {
      return TabButtonPosition.BOTTOM;
    }
    // Default to CENTER
    return TabButtonPosition.CENTER;
  };

  const topTabs = createMemo((): VerticalTabAnimated<T>[] => {
    return tabs().filter(
      (tab) => getEffectivePosition(tab) === TabButtonPosition.TOP
    );
  });

  const centerTabs = createMemo((): VerticalTabAnimated<T>[] => {
    return tabs().filter(
      (tab) => getEffectivePosition(tab) === TabButtonPosition.CENTER
    );
  });

  const bottomTabs = createMemo((): VerticalTabAnimated<T>[] => {
    return tabs().filter(
      (tab) => getEffectivePosition(tab) === TabButtonPosition.BOTTOM
    );
  });

  const handleTabChange = (tab: VerticalTabAnimated<T>): void => {
    const isOpen = selectedTab() === tab.name;
    const hasContent = !!tab.content && !tab.disabled;

    if (isOpen) {
      setSelectedTab(undefined);
      props.onTabToggle?.(tab.name, false);
    } else {
      if (hasContent) {
        setSelectedTab(() => tab.name);
      }
      props.onTabToggle?.(tab.name, true);
    }
  };

  const showTab = createMemo((): VerticalTabAnimated<T> | undefined => {
    // Use displayTab to keep content mounted during close animation
    const tab = tabs().find((tab) => tab.name === displayTab());
    if (!tab || !tab.content || tab.disabled) return undefined;
    return tab;
  });

  const tabContent = (): JSX.Element => {
    return (
      <Show when={showTab()} keyed>
        {(tab) => (
          <AnimatedTabPanelContent
            tab={tab}
            tabButtonStyle={props.tabButtonStyle}
            menuPosition={props.menuPosition}
          />
        )}
      </Show>
    );
  };

  createEffect(() => {
    setSelectedTab(() => props.selectedTab);
  });

  createEffect(() => {
    setTabs(() => props.tabs);
  });

  // Manage displayTab for animation: delay unmounting during close animation
  createEffect(() => {
    const current = selectedTab();
    if (current) {
      setDisplayTab(() => current);
    } else {
      const timer = setTimeout(() => {
        setDisplayTab(() => undefined);
      }, ANIMATION_DURATION_MS);

      onCleanup(() => clearTimeout(timer));
    }
  });

  /**
   * Helper to get tooltip text from either string or object format
   */
  const getTooltipText = (
    tooltip: VerticalTabAnimated<T>['tooltip']
  ): string => {
    if (!tooltip) return '';
    if (typeof tooltip === 'string') return tooltip;
    return tooltip.text;
  };

  /**
   * Helper to get tooltip position from object format
   */
  const getTooltipPosition = (
    tooltip: VerticalTabAnimated<T>['tooltip']
  ): 'top' | 'bottom' | 'left' | 'right' | 'auto' | undefined => {
    if (!tooltip || typeof tooltip === 'string') return undefined;
    return tooltip.position;
  };

  /**
   * Helper to format tooltip text with keyboard shortcut
   */
  const getFormattedTooltipText = (
    tooltip: VerticalTabAnimated<T>['tooltip']
  ): string => {
    if (!tooltip) return '';
    if (typeof tooltip === 'string') return tooltip;

    const text = tooltip.text;
    const shortcut = tooltip.shortCut;

    return shortcut ? `${text} (${shortcut})` : text;
  };

  /**
   * Creates a Connect style tab button element
   */
  const connectButton = (tab: VerticalTabAnimated<T>): JSX.Element => {
    return (
      <div
        role="tab"
        id={`vertical-tab-menu-${tab.name}`}
        aria-controls={`vertical-tab-content-${tab.name}`}
        aria-selected={selectedTab() === tab.name}
        aria-label={getTooltipText(tab.tooltip)}
        data-testid={`vertical-tab-menu-${tab.name}`}
        class={`px-2 py-2 flex items-center justify-center text-base-content ${
          tab.disabled ? '' : 'cursor-pointer'
        } ${
          tab.disabled
            ? 'opacity-25 text-base-muted'
            : selectedTab() === tab.name
              ? 'bg-base-page text-primary'
              : 'hover:bg-base-200'
        }`}
        onClick={() => {
          if (tab.disabled) return;
          handleTabChange(tab);
        }}
      >
        <i
          aria-hidden="true"
          class={`${tab.iconClass ?? 'icon-font'} ${tab.icon ?? ''}`}
        >
          {tab.iconClass ? tab.icon : ''}
        </i>
      </div>
    );
  };

  /**
   * Creates a Modus toolbar style button element
   * Based on Modus Design System: https://modus.trimble.com/components/web/toolbar/styles/
   * - 32x32 secondary icon-only button
   * - Shadow Level 3 for floating effect
   * - Rounded corners
   */
  const modusButton = (tab: VerticalTabAnimated<T>): JSX.Element => {
    return (
      <div class="w-full flex items-center justify-center">
        <button
          type="button"
          role="tab"
          id={`vertical-tab-menu-${tab.name}`}
          aria-controls={`vertical-tab-content-${tab.name}`}
          aria-selected={selectedTab() === tab.name}
          aria-label={getFormattedTooltipText(tab.tooltip)}
          data-testid={`vertical-tab-menu-${tab.name}`}
          disabled={tab.disabled}
          class={`w-8 h-8 flex items-center justify-center rounded ${
            tab.disabled ? '' : 'cursor-pointer'
          } ${
            tab.disabled
              ? 'opacity-25 bg-base-200 text-base-muted'
              : selectedTab() === tab.name
                ? 'bg-base-100 text-base-content'
                : 'bg-base-page text-base-content hover:bg-base-100'
          } transition-all duration-200`}
          onClick={() => {
            if (tab.disabled) return;
            handleTabChange(tab);
          }}
        >
          <i
            aria-hidden="true"
            class={`${tab.iconClass ?? 'icon-font'} ${tab.icon ?? ''} text-2xl leading-6`}
          >
            {tab.iconClass ? tab.icon : ''}
          </i>
        </button>
      </div>
    );
  };

  const renderTabs = (tabs: VerticalTabAnimated<T>[]): JSX.Element => {
    return (
      <div
        class={`flex flex-col w-full ${
          props.tabButtonStyle === ButtonStyle.MODUS ? 'gap-1' : ''
        }`}
      >
        <For each={tabs}>
          {(tab) => {
            const isObjectTooltip =
              tab.tooltip && typeof tab.tooltip === 'object';

            // Choose button style based on panel-level tabButtonStyle property
            const buttonStyle = props.tabButtonStyle ?? ButtonStyle.CONNECT;
            const tabElement =
              buttonStyle === ButtonStyle.MODUS
                ? modusButton(tab)
                : connectButton(tab);

            return (
              (tab.iconVisible ?? true) !== false && (
                <Show when={isObjectTooltip} fallback={tabElement}>
                  <TooltipDelayed
                    text={getFormattedTooltipText(tab.tooltip)}
                    position={getTooltipPosition(tab.tooltip)}
                  >
                    {tabElement}
                  </TooltipDelayed>
                </Show>
              )
            );
          }}
        </For>
      </div>
    );
  };

  /**
   * Renders the vertical menu with tab buttons positioned at top, center, and bottom
   */
  const renderMenu = (): JSX.Element => {
    const isRight = props.menuPosition === MenuPosition.RIGHT;
    const isModus = props.tabButtonStyle === ButtonStyle.MODUS;

    const topSpacingPx =
      props.topSpacing ?? (isModus ? DEFAULT_MODUS_SPACING_PX : 0);

    const bottomSpacingPx =
      props.bottomSpacing ?? (isModus ? DEFAULT_MODUS_SPACING_PX : 0);

    return (
      <div
        data-testid="vertical-tab-menu"
        class={`flex flex-col h-full z-10 ${
          isRight ? 'absolute top-0 right-0' : 'relative'
        } ${
          isModus
            ? `bg-base-page ${isRight ? 'shadow-lg border-l border-base-200' : ''}`
            : 'bg-base-100'
        }`}
        style={{ width: `${menuWidth()}px` }}
      >
        <div
          style={{
            // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
            'margin-top': `${topSpacingPx}px`,
          }}
        >
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {renderTabs(topTabs())}
        </div>
        <div
          role="tablist"
          aria-orientation="vertical"
          class="flex flex-col flex-grow justify-center"
        >
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {renderTabs(centerTabs())}
        </div>
        <div
          class="mt-auto"
          style={{
            // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
            'margin-bottom': `${bottomSpacingPx}px`,
          }}
        >
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {renderTabs(bottomTabs())}
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="vertical-tab-panel"
      class="h-full transition-all ease-in-out overflow-hidden relative"
      style={{
        width: isPanelOpen()
          ? `${menuWidth() + contentWidth()}px`
          : `${menuWidth()}px`,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
        'transition-duration': `${ANIMATION_DURATION_MS}ms`,
      }}
    >
      <div
        class={`h-full min-h-0 flex ${props.menuPosition === MenuPosition.RIGHT ? 'justify-end' : ''}`}
        style={{ width: `${menuWidth() + contentWidth()}px` }}
      >
        <Show when={props.menuPosition === MenuPosition.LEFT}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          <Show when={props.menuVisible !== false}>{renderMenu()}</Show>
          <div
            class="flex h-full min-h-0 flex-col transition-transform ease-in-out relative z-0 shrink-0"
            style={{
              width: `${contentWidth()}px`,
              transform: isPanelOpen()
                ? 'translateX(0)'
                : `translateX(-${contentWidth()}px)`,
              // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
              'transition-duration': `${ANIMATION_DURATION_MS}ms`,
            }}
          >
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {tabContent()}
          </div>
        </Show>
        <Show when={props.menuPosition === MenuPosition.RIGHT}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          <Show when={props.menuVisible !== false}>{renderMenu()}</Show>
          <div
            class="flex h-full min-h-0 flex-col transition-transform ease-in-out relative z-0 shrink-0"
            style={{
              width: `${contentWidth() + menuWidth()}px`,
              // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
              'padding-right': `${menuWidth()}px`,
              transform: isPanelOpen()
                ? 'translateX(0)'
                : `translateX(${contentWidth()}px)`,
              // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSS properties use kebab-case
              'transition-duration': `${ANIMATION_DURATION_MS}ms`,
            }}
          >
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {tabContent()}
          </div>
        </Show>
      </div>
    </div>
  );
};
